import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useCurrency } from "@/context/CurrencyContext";
import { arDigits, fmtLocaleNum, fmtAED, fmtIsoWeekLabel, fmtIsoDate } from "@/lib/utils";
import WeeklyInterpretation from "@/components/insights/WeeklyInterpretation";
import { weekValueBn, displayBiggestSale } from "@/lib/weekValue";

/** /insights/weekly/:iso_week, full weekly recap page with DLD citations.
 *  Arabic mode renders every figure in Eastern Arabic-Indic digits (٠-٩)
 *  and swaps "AED" for "د.إ" so visitors who don't read Latin numbers
 *  still get the full recap. */
export default function InsightsWeekly() {
  const { iso_week } = useParams();
  const { lang, lh } = useLang();
  const isAR = lang === "ar";
  const { format, formatBn } = useCurrency();
  const [week, setWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // V2.0 P2 — auto-interpretation needs (1) the trailing weeks (to build the
  // 4-week rolling average) and (2) the YTD summary block. Both come from
  // the same /insights/hub payload we already serve on the /insights hub —
  // no new endpoints.
  const [hub, setHub] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/insights/weekly/${iso_week}`).then((r) => setWeek(r.data)),
      api.get(`/insights/hub`).then((r) => setHub(r.data)).catch(() => setHub(null)),
    ])
      .catch((e) => setError(e?.response?.status === 404 ? "notfound" : "error"))
      .finally(() => setLoading(false));
  }, [iso_week]);

  // Prior weeks strictly BEFORE the current one, DESC by iso_week.
  const priorWeeks = React.useMemo(() => {
    if (!hub?.latest_weeks || !week) return [];
    return hub.latest_weeks.filter((w) => w.iso_week < week.iso_week);
  }, [hub, week]);

  const source = week?.source;
  const valueBn = weekValueBn(week);
  const notFound = error === "notfound";
  const canonical = `https://thepropertic.ae/insights/weekly/${iso_week}${isAR ? "?lang=ar" : ""}`;
  // JSON-LD stays in Latin digits — schema.org consumers (search engines,
  // AI crawlers) expect ASCII numbers regardless of the user's language.
  const jsonLd = week && {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: `Dubai weekly real estate transactions, ${week.iso_week}`,
    description: `Dubai Land Department transaction summary for ISO week ${week.iso_week} (${week.start_date} → ${week.end_date}). ${week.tx_count} transactions totalling AED ${week.total_aed_bn}B.`,
    url: canonical,
    creator: { "@type": "GovernmentOrganization", name: "Dubai Land Department" },
    license: "https://opendata.dubai.gov.ae/",
    temporalCoverage: `${week.start_date}/${week.end_date}`,
    spatialCoverage: { "@type": "Place", name: "Dubai, United Arab Emirates" },
    isBasedOn: source?.url,
    datePublished: source?.refreshed || undefined,
  };
  // BreadcrumbList schema — surfaces "Home › Insights › Weekly › W37" in
  // Google search results, lifting CTR on informational pages.
  const breadcrumbLd = week && {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "The Propertic", item: "https://thepropertic.ae/" },
      { "@type": "ListItem", position: 2, name: "Insights", item: "https://thepropertic.ae/insights" },
      { "@type": "ListItem", position: 3, name: `Weekly ${week.iso_week}`, item: canonical },
    ],
  };

  return (
    <div className="min-h-screen bg-propertic-paper text-propertic-navy" dir={isAR ? "rtl" : "ltr"}>
      <Helmet>
        <title>{`${iso_week} · ${isAR ? "تحليلات دبي الأسبوعية" : "Dubai Weekly Insights"} · The Propertic`}</title>
        {notFound
          ? <meta name="robots" content="noindex, follow" />
          : <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />}
        <meta name="description" content={week
          ? (isAR
              ? `ملخص أسبوع ${week.iso_week} لسوق دبي، ${fmtLocaleNum(week.tx_count, isAR)} صفقة موثّقة من دائرة الأراضي والأملاك بإجمالي ${formatBn(valueBn || 0)}. بيانات رسمية محدَّثة أسبوعياً.`
              : `Week ${week.iso_week} Dubai property recap · ${fmtLocaleNum(week.tx_count, isAR)} DLD transactions totalling ${formatBn(valueBn || 0)}. Dubai Land Department official open data.`)
          : (isAR
              ? "ملخص أسبوعي لصفقات دبي العقارية من البيانات المفتوحة الرسمية لدائرة الأراضي والأملاك. تُحدَّث كل اثنين ١١:٠٠ بتوقيت الإمارات المحلي."
              : "Weekly Dubai property transactions recap from Dubai Land Department official open data. Refreshed every Monday 11:00 UAE local time.")} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${iso_week} · ${isAR ? "تحليلات دبي الأسبوعية" : "Dubai Weekly Insights"} · The Propertic`} />
        <meta property="og:description" content={week
          ? (isAR
              ? `ملخص أسبوع ${week.iso_week} لسوق دبي، ${fmtLocaleNum(week.tx_count, isAR)} صفقة موثّقة من دائرة الأراضي والأملاك بإجمالي ${formatBn(valueBn || 0)}.`
              : `Week ${week.iso_week} Dubai property recap · ${fmtLocaleNum(week.tx_count, isAR)} DLD transactions totalling ${formatBn(valueBn || 0)}.`)
          : (isAR
              ? "ملخص أسبوعي لصفقات دبي العقارية من البيانات المفتوحة الرسمية لدائرة الأراضي والأملاك."
              : "Weekly Dubai property transactions recap from Dubai Land Department official open data.")} />
        <meta property="og:image" content="https://thepropertic.ae/og-share-logo.png" />
        <meta property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${iso_week} · ${isAR ? "تحليلات دبي الأسبوعية" : "Dubai Weekly Insights"} · The Propertic`} />
        {!notFound && <link rel="canonical" href={canonical} />}
        {!notFound && <link rel="alternate" hrefLang="en" href={`https://thepropertic.ae/insights/weekly/${iso_week}`} />}
        {!notFound && <link rel="alternate" hrefLang="ar" href={`https://thepropertic.ae/insights/weekly/${iso_week}?lang=ar`} />}
        {!notFound && <link rel="alternate" hrefLang="x-default" href={`https://thepropertic.ae/insights/weekly/${iso_week}`} />}
        {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
        {breadcrumbLd && <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>}
      </Helmet>

      <div className="pt-28 sm:pt-36 pb-16 px-4 sm:px-8 lg:px-16">
        <div className="max-w-4xl mx-auto">
          <Link
            to={lh("/insights")}
            data-testid="insights-back"
            className="inline-flex items-center gap-2 text-[11px] font-mono-brand tracking-[0.16em] uppercase text-propertic-muted hover:text-propertic-gold mb-6"
          >
            <ArrowLeft className={`w-3.5 h-3.5 ${isAR ? "rotate-180" : ""}`} />
            {isAR ? "كل التحليلات" : "All insights"}
          </Link>

          {loading && <p className="text-sm text-propertic-muted">{isAR ? "جاري التحميل…" : "Loading…"}</p>}

          {notFound && (
            <div className="rounded-2xl border hairline bg-white p-8 text-center" data-testid="week-not-found">
              <h1 className="font-editorial text-2xl mb-2">{isAR ? "لم نجد بيانات لهذا الأسبوع" : "No data for this week"}</h1>
              <p className="text-sm text-propertic-muted">{isAR ? arDigits(iso_week) : iso_week}</p>
            </div>
          )}

          {error === "error" && (
            <div className="rounded-2xl border hairline bg-white p-8 text-center" data-testid="week-load-error">
              <p className="font-editorial text-xl mb-2">{isAR ? "تعذّر تحميل البيانات حالياً" : "Data temporarily unavailable"}</p>
              <p className="text-sm text-propertic-muted">{isAR ? "يرجى المحاولة بعد قليل." : "Please try again shortly."}</p>
            </div>
          )}

          {week && (
            <>
              <p className="font-mono-brand text-[10px] tracking-[0.2em] uppercase text-propertic-gold mb-2">
                {isAR ? "تحليل أسبوعي · دائرة الأراضي والأملاك" : "Weekly recap · Dubai Land Department"}
              </p>
              <h1 className="font-editorial text-3xl sm:text-4xl lg:text-5xl leading-tight mb-2" data-testid="week-title">
                {isAR ? `عقارات دبي · ${fmtIsoWeekLabel(week.iso_week, true)}` : `Dubai property week ${week.iso_week}`}
              </h1>
              <p className="text-sm text-propertic-muted mb-8">{fmtIsoDate(week.start_date, isAR)} → {fmtIsoDate(week.end_date, isAR)}</p>

              {/* W22 coincided with the Eid al-Adha public holidays; the value IS published (total_aed). */}
              {iso_week === "2026-W22" && (
                <div className="mb-8 rounded-2xl border border-amber-300/60 bg-amber-50 p-4 sm:p-5" data-testid="week-eid-note">
                  <p className="font-mono-brand text-amber-800 text-[9px] tracking-[0.2em] uppercase mb-1.5">
                    {isAR ? "ملاحظة تحريرية" : "Editorial note"}
                  </p>
                  <p className="text-[13px] text-amber-900 leading-relaxed">
                    {isAR
                      ? "تزامن هذا الأسبوع مع عطلة عيد الأضحى الرسمية في الإمارات، وانخفض فيه عدد الإجراءات المسجّلة لدى دائرة الأراضي والأملاك إلى نحو عُشر المعدّل الأسبوعي المعتاد. جميع الأرقام المعروضة، بما فيها القيمة الإجمالية، مأخوذة من سجلات البيانات المفتوحة المنشورة لهذا الأسبوع."
                      : "This week coincided with the UAE Eid al-Adha public holidays, and recorded DLD procedures fell to roughly a tenth of the usual weekly pace. All figures shown, including total value, come from the DLD Open Data records published for this week."}
                  </p>
                </div>
              )}

              {/* V2.0 P2 — Auto-interpretation block. Deterministic, math-only
                  reading of the week vs YTD share + 4-week trailing average.
                  Rendered above the raw headline stats so readers get the
                  interpretation before the numbers, not after. */}
              {hub?.summary && (
                <WeeklyInterpretation
                  week={week}
                  priorWeeks={priorWeeks}
                  summary={hub.summary}
                  variant="expanded"
                />
              )}

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="week-headline-stats">
                {[
                  { k: isAR ? "إجمالي الإجراءات" : "All DLD procedures", v: fmtLocaleNum(week.tx_count, isAR), h: isAR ? "بيع + رهون + هبات" : "sales + mortgages + gifts" },
                  { k: isAR ? "إجمالي القيمة" : "Total value", v: valueBn == null ? "—" : formatBn(valueBn), h: null },
                  { k: isAR ? "المبيعات العقارية" : "Property sales only", v: fmtLocaleNum(week.sales_count, isAR), h: isAR ? "بيع فعلي" : "pure sales" },
                  { k: isAR ? "حصة قيد الإنشاء (كل الإجراءات)" : "Off-plan share (all procedures)", v: week.offplan_share_pct != null ? (isAR ? `${arDigits(week.offplan_share_pct)}٪` : `${week.offplan_share_pct}%`) : "—", h: isAR ? "= إجراءات قيد الإنشاء ÷ إجمالي إجراءات الدائرة" : "= off-plan procedures ÷ all DLD procedures" },
                ].map((s, i) => (
                  <div key={i} className="rounded-2xl border hairline bg-white p-5">
                    <p className="font-mono-brand text-[9px] tracking-[0.16em] uppercase text-propertic-muted mb-2">{s.k}</p>
                    <p className="font-editorial text-2xl sm:text-3xl tabular-nums">{s.v}</p>
                    {s.h && <p className="mt-1.5 text-[10px] text-propertic-muted leading-snug">{s.h}</p>}
                  </div>
                ))}
              </div>

              {/* Weekly methodology note — clarifies these are DLD records as
                  published, not seasonally adjusted or normalised. */}
              <p className="mb-8 text-[11px] text-propertic-muted leading-relaxed" data-testid="week-methodology-note">
                {isAR
                  ? "الأرقام الأسبوعية تعكس سجلات دائرة الأراضي والأملاك كما تم استرجاعها للفترة المحددة، دون أي تعديل موسمي أو تسوية يدوية."
                  : "Weekly figures reflect DLD records retrieved for the stated period and are not seasonally adjusted or manually normalized."}
              </p>

              {displayBiggestSale(week) && (() => { const bs = displayBiggestSale(week); return (
                <div className="rounded-2xl border border-propertic-gold/40 bg-white p-6 mb-6" data-testid="week-biggest-sale">
                  <p className="font-mono-brand text-[10px] tracking-[0.16em] uppercase text-propertic-gold mb-1">
                    {isAR ? "أعلى صفقة في الأسبوع" : "Biggest sale this week"}
                  </p>
                  <p className="font-editorial text-2xl sm:text-3xl mb-2">{format(bs.value_aed)}</p>
                  <p className="text-sm text-propertic-ink-2">
                    {bs.area}
                    {bs.project ? ` · ${bs.project}` : ""}
                    {bs.rooms ? ` · ${bs.rooms}` : ""}
                    {bs.is_offplan ? (isAR ? " · خارطة" : " · off-plan") : (isAR ? " · جاهز" : " · ready")}
                  </p>
                </div>
              ); })()}

              {/* Most-affordable card intentionally removed from public /insights (2026-09-09).
                  Low-end DLD transactions (studios + 1BR near AED 200K) are almost always
                  distressed or off-plan takeovers and mis-represent the Dubai apartment
                  market. Roni only surfaces the figure when a visitor explicitly asks,
                  with a mandatory disclaimer + licensed-advisor CTA. */}

              <div className="grid grid-cols-2 gap-4 mb-10">
                <div className="rounded-2xl border hairline bg-white p-5">
                  <p className="font-mono-brand text-[9px] tracking-[0.16em] uppercase text-propertic-muted mb-1">{isAR ? "الوسيط لسعر البيع" : "Median sale price"}</p>
                  <p className="font-editorial text-xl tabular-nums">{format(week.median_sale_aed)}</p>
                </div>
                <div className="rounded-2xl border hairline bg-white p-5">
                  <p className="font-mono-brand text-[9px] tracking-[0.16em] uppercase text-propertic-muted mb-1">{isAR ? "الوسيط لسعر القدم²" : "Median AED / sqft"}</p>
                  <p className="font-editorial text-xl tabular-nums">{format(week.median_aed_per_sqft)}</p>
                </div>
              </div>

              {source && (
                <div className="rounded-2xl border hairline bg-propertic-sand/40 p-5 text-[12px] leading-relaxed" data-testid="week-source-block">
                  <p className="font-mono-brand text-[10px] tracking-[0.14em] uppercase text-propertic-navy mb-2">
                    {isAR ? "المصدر والترخيص" : "Source & license"}
                  </p>
                  <p>
                    {isAR ? "جميع الأرقام مأخوذة مباشرة من " : "All figures pulled directly from "}
                    <a href={source.url} target="_blank" rel="noreferrer" className="text-propertic-gold underline">
                      {isAR ? source.name_ar : source.name}
                    </a>
                    {isAR
                      ? `، التغطية: ${fmtIsoDate(week.start_date, true)} → ${fmtIsoDate(week.end_date, true)}${source.refreshed ? ` · آخر تحديث ناجح للبيانات: ${fmtIsoDate(source.refreshed.slice(0, 10), true)}` : ""}. `
                      : ` (coverage ${week.start_date} → ${week.end_date}${source.refreshed ? ` · dataset last refreshed ${source.refreshed.slice(0, 10)}` : ""}). `}
                    {isAR ? "رخصة دبي للبيانات المفتوحة · يُسمح بإعادة النشر التجاري مع ذكر المصدر." : source.license}
                  </p>
                </div>
              )}

              {/* Related internal-link strip. Weekly Insights is a high-
                  traffic entry point for organic search, so linking out to
                  Compare Areas, Mortgage Calculator and Roni keeps visitors
                  moving deeper into the funnel and helps internal PageRank. */}
              <div className="mt-8 pt-6 border-t hairline" data-testid="week-related-strip">
                <p className="font-mono-brand text-[10px] tracking-[0.2em] uppercase text-propertic-gold mb-3">
                  {isAR ? "استكمل التصفح" : "Keep exploring"}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Link
                    to={lh("/dubai-compare-areas")}
                    data-testid="week-related-compare"
                    className="group rounded-xl border hairline bg-white p-4 hover:border-propertic-gold hover:shadow-lg transition-all"
                  >
                    <p className="font-mono-brand text-[9px] tracking-[0.16em] uppercase text-propertic-muted mb-1">
                      {isAR ? "الخطوة التالية" : "Next step"}
                    </p>
                    <p className="font-editorial text-[15px] text-propertic-navy leading-tight">
                      {isAR ? "قارن مجمّعات دبي" : "Compare Dubai Communities"}
                    </p>
                    <p className="mt-1.5 text-[11px] text-propertic-ink-2 leading-snug">
                      {isAR ? "AED/قدم²، عائد إجمالي، صفقات YTD ٢٠٢٦ جنباً إلى جنب." : "AED/sqft, gross yield, YTD 2026 txns side-by-side."}
                    </p>
                  </Link>
                  <Link
                    to={lh("/mortgage-calculator")}
                    data-testid="week-related-mortgage"
                    className="group rounded-xl border hairline bg-white p-4 hover:border-propertic-gold hover:shadow-lg transition-all"
                  >
                    <p className="font-mono-brand text-[9px] tracking-[0.16em] uppercase text-propertic-muted mb-1">
                      {isAR ? "احسب" : "Calculate"}
                    </p>
                    <p className="font-editorial text-[15px] text-propertic-navy leading-tight">
                      {isAR ? "حاسبة الرهن العقاري" : "Mortgage Calculator"}
                    </p>
                    <p className="mt-1.5 text-[11px] text-propertic-ink-2 leading-snug">
                      {isAR ? "قدّر القسط الشهري وفق معدّل فائدة إرشادي قابل للتعديل، مع بيان الرسوم غير المشمولة. المعدّل الافتراضي افتراض للحساب وليس عرضاً بنكياً." : "Estimate EMI with an editable illustrative interest rate and transparent fee exclusions. The default rate is a manually reviewed assumption, not a live bank quote."}
                    </p>
                  </Link>
                  <Link
                    to={lh("/aiagent")}
                    data-testid="week-related-roni"
                    className="group rounded-xl border hairline bg-white p-4 hover:border-propertic-gold hover:shadow-lg transition-all"
                  >
                    <p className="font-mono-brand text-[9px] tracking-[0.16em] uppercase text-propertic-muted mb-1">
                      {isAR ? "اسأل روني" : "Ask Roni"}
                    </p>
                    <p className="font-editorial text-[15px] text-propertic-navy leading-tight">
                      {isAR ? "المساعد العقاري الذكي" : "AI Real-Estate Advisor"}
                    </p>
                    <p className="mt-1.5 text-[11px] text-propertic-ink-2 leading-snug">
                      {isAR ? "قواعد CBUAE للرهن، والمقارنات بين المناطق." : "CBUAE mortgage rules and community comparisons."}
                    </p>
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
